/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React from "react";
import get from "lodash/get";

import { LazyLoadImages, ResponsiveHeroImage, ReviewRating } from "@quintype/components";
import { CustomLink } from "../custom-link";
import { ImageFallback } from "../basic/image-fallback";
import { Author } from "../basic/author";
import { altTagOfHeroImages, getScrollSpeed, isExternalStory, getStoryUrl } from "../../utils";
import PropTypes from "prop-types";
import { GlideCarousel } from "../../components/atoms/glide";

function Slider(props) {
  const { eager = false } = props;

  let resizeDone = false;

  const resize = () => {
    if (resizeDone === false) {
      global.dispatchEvent(new global.Event("resize"));
      resizeDone = true;
    }
  };

  const options = {
    numberOfStoriesToShow: 6,
    afterSlide: resize,
    perView: 1,
    arrow: true,
    items: props.stories,
    dot: "",
    autoplay: getScrollSpeed(props.associatedMetadata)
  };

  const SliderWrapper = (
    <GlideCarousel {...options} className="carousel">
      {props.stories.slice(0, props.numberOfStoriesToShow).map((storyObj, index) => {
        const story = storyObj && storyObj.story ? storyObj.story : storyObj;
        const initialVisibleSlidesEager = index === 0 ? eager : false;
        return (
          <div key={story.id}>
            {React.createElement(props.itemTemplate || CarouselItem, {
              story: story,
              config: props.config,
              associatedMetadata: props.associatedMetadata,
              collection: props.collection,
              eager: initialVisibleSlidesEager
            })}
          </div>
        );
      })}
    </GlideCarousel>
  );

  if (!props.stories) return null;

  return (
    <div className="carousel-wrapper">
      <div
        className={`carousel slider ${
          get(props.config, ["theme-attributes", "language_direction"]) === "right" ? "dots-rtl" : ""
        }`}
      >
        {SliderWrapper}
      </div>
    </div>
  );
}

Slider.propTypes = {
  associatedMetadata: PropTypes.object,
  stories: PropTypes.array,
  arrows: PropTypes.bool,
  config: PropTypes.shape({
    "theme-attributes": PropTypes.object,
    language_direction: PropTypes.string
  }),
  collection: PropTypes.object,
  numberOfStoriesToShow: PropTypes.number,
  itemTemplate: PropTypes.func,
  autoplay_duration_sec: PropTypes.number,
  eager: PropTypes.bool
};

function CarouselItem(props) {
  const { story, collection, config = {}, associatedMetadata = {}, eager = false } = props;

  if (!story) return null;
  return (
    <CustomLink href={"" + getStoryUrl(story)} externalLink={isExternalStory(story)}>
      <Slide
        eager={eager}
        associatedMetadata={associatedMetadata}
        story={story}
        config={config}
        collection={collection}
      />
    </CustomLink>
  );
}
CarouselItem.propTypes = {
  story: PropTypes.object,
  collection: PropTypes.object,
  config: PropTypes.object,
  associatedMetadata: PropTypes.object,
  eager: PropTypes.bool
};
function authorImageUrl(author) {
  if (!author || !author["avatar-url"]) return;

  // FIXME: Hack. Better to use FocusImage class from Quintype-JS
  if (author["avatar-s3-key"] && author["avatar-s3-key"] !== "") {
    return `${author["avatar-url"]}?w=96&h=96`;
  }

  return author["avatar-url"];
}

function Slide(props) {
  const {
    story: { "story-template": storyTemplate },
    eager = false
  } = props;
  const isReviewStory = props.story["story-template"] === "review";
  return (
    <div>
      <div
        className={`carousel-slide ${
          get(props.config, ["theme-attributes", "language_direction"]) === "right" ? "rtl-section-header" : ""
        }`}
      >
        <div className="carousel-slide__image">
          {props.story["hero-image-s3-key"] ? (
            <figure className={`carousel-slide__image__${storyTemplate}`}>
              <LazyLoadImages margin={"0px"}>
                <ResponsiveHeroImage
                  story={props.story}
                  aspectRatio={[4, 3]}
                  alt={altTagOfHeroImages(props.story)}
                  defaultWidth={480}
                  widths={[250, 480, 640]}
                  sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 23%"
                  imgParams={{ auto: ["format", "compress"], fit: "max" }}
                  eager={eager}
                />
              </LazyLoadImages>
            </figure>
          ) : (
            <ImageFallback story={props.story} />
          )}
        </div>
        <div className="carousel-slide__content">
          {" "}
          (
          <div className="carousel-slide__section" data-testid="carousel-sectionname">
            {get(props.story, ["sections", 0, "display-name"]) || get(props.story, ["sections", 0, "name"], "")}
          </div>
          <h2 className="carousel-slide__heading" data-test-id="carousel-story-headline">
            <bdi dangerouslySetInnerHTML={{ __html: props.story.headline }} />
          </h2>
          {isReviewStory && <ReviewRating value={get(props.story, ["metadata", "review-rating", "value"], "0")} />}
          <div className="post-details">
            <div data-testid="carousel-authorname">
              <Author
                author={Object.assign({}, get(props.story, ["authors", 0]), {
                  name: get(props.story, ["authors", 0, "name"], props.story["author-name"]),
                  image: authorImageUrl(get(props.story, ["authors", 0])),
                  id: get(props.story, ["authors", 0, "id"], props.story["author-id"])
                })}
                config={props.config}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
Slide.propTypes = {
  story: PropTypes.object,
  associatedMetadata: PropTypes.shape({
    show_section_tag: PropTypes.bool,
    show_author_name: PropTypes.bool,
    show_time_of_publish: PropTypes.bool
  }),
  config: PropTypes.object,
  eager: PropTypes.bool
};
export { Slider };
